import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { Search, Grid, List, Star, Award, FileCheck, Ticket, Package, FileText, Camera, Music, Bookmark } from "lucide-react";
import { api } from "@/lib/api";

/* ============================================================================
   BB-067A: Elvis Aloha Archive — Collection Gallery
   BB-067 FE-001: Compact Sticky Navigation Refinement
   
   Key Design Elements:
   - Compact sticky category navigation beneath header
   - Category tabs with counts (coral active state)
   - Artifact cards with status badges
   - Sharp corners (no rounded-lg)
   ============================================================================ */

const PSA_ACTIVE = new Set(["Submitted to PSA", "Graded", "Returned from PSA"]);

/* Icon mapping for category types */
const getCategoryIcon = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes('ticket')) return Ticket;
  if (lower.includes('proof') || lower.includes('design')) return Ticket;
  if (lower.includes('negative') || lower.includes('production')) return Package;
  if (lower.includes('document') || lower.includes('graceland')) return FileText;
  if (lower.includes('photo')) return Camera;
  if (lower.includes('recording') || lower.includes('audio')) return Music;
  if (lower.includes('memorabilia') || lower.includes('artifact')) return Bookmark;
  return Package; // Default
};

/* Shorten category names for compact display */
const getCompactLabel = (name) => {
  if (name === 'Aloha Tickets') return 'Tickets';
  if (name === 'Ticket Designs & Proofs') return 'Proofs';
  if (name === 'Negatives & Production Materials') return 'Production';
  if (name === 'Graceland Display Documents') return 'Graceland';
  if (name === 'PSA Graded Items') return 'PSA';
  if (name === 'Related Aloha Artifacts') return 'Artifacts';
  return name.length > 12 ? name.substring(0, 10) + '...' : name;
};

export const CollectionGalleryPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [flags, setFlags] = useState({ featured: false, graceland: false, psa: false });
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("newest");

  const activeCategory = searchParams.get("category") || "All";

  useEffect(() => {
    Promise.all([api.get("/collection/items"), api.get("/collection/categories")])
      .then(([i, c]) => {
        setItems(i.data);
        setCategories(c.data.filter((x) => x.count > 0));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setCategory = (name) => {
    if (name === "All") setSearchParams({}, { replace: true });
    else setSearchParams({ category: name }, { replace: true });
  };

  /* Check if a category is currently active */
  const isCategoryActive = (categoryName) => {
    return activeCategory === categoryName;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (activeCategory !== "All" && it.category !== activeCategory) return false;
      if (flags.featured && !it.featured) return false;
      if (flags.graceland && !it.graceland_displayed) return false;
      if (flags.psa && !PSA_ACTIVE.has(it.psa_status)) return false;
      if (q) {
        const hay = `${it.title} ${it.short_description || ""} ${it.category}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, activeCategory, flags, query]);

  const toggle = (k) => setFlags((f) => ({ ...f, [k]: !f[k] }));

  const clearFilters = () => {
    setFlags({ featured: false, graceland: false, psa: false });
    setQuery("");
    setCategory("All");
  };

  const hasActiveFilters = flags.featured || flags.graceland || flags.psa || query.trim().length > 0;

  return (
    <div className="min-h-screen bg-[#F5F0E8]" data-testid="collection-gallery-page">
      
      {/* ========== COMPACT STICKY CATEGORY NAVIGATION ========== */}
      <nav 
        className="collection-category-nav"
        aria-label="Collection categories"
        data-testid="collection-category-nav"
      >
        <style>{`
          /* BB-067 FE-001: Compact Sticky Collection Navigation */
          .collection-category-nav {
            position: sticky;
            top: var(--public-header-height, 120px);
            z-index: 30;
            background: rgba(247, 241, 229, 0.98);
            border-bottom: 1px solid var(--color-gold-rule, #D2C19F);
            backdrop-filter: blur(8px);
          }
          
          .collection-category-nav__inner {
            width: min(calc(100% - (var(--public-gutter-desktop, 48px) * 2)), var(--public-width-wide, 1440px));
            margin-inline: auto;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 6px;
            padding-block: 8px;
          }
          
          .collection-category-nav__item {
            min-height: 52px;
            padding: 8px 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            color: var(--color-navy, #0D273C);
            background: transparent;
            border: 1px solid transparent;
            border-radius: 4px;
            text-decoration: none;
            transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
          }
          
          .collection-category-nav__icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
          }
          
          .collection-category-nav__icon svg {
            width: 18px;
            height: 18px;
            stroke-width: 1.5;
          }
          
          .collection-category-nav__label {
            font-family: 'Source Sans 3', sans-serif;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }
          
          .collection-category-nav__count {
            font-family: 'Source Sans 3', sans-serif;
            font-size: 11px;
            font-weight: 500;
            color: var(--text-muted, #7D7872);
            background: var(--color-parchment, #EAE3D5);
            padding: 2px 6px;
            border-radius: 10px;
          }
          
          .collection-category-nav__item:hover {
            background: var(--color-parchment, #EAE3D5);
            border-color: var(--color-gold-rule, #D2C19F);
          }
          
          .collection-category-nav__item.is-active {
            color: var(--color-coral, #D85C4A);
            background: #fffaf1;
            border-color: var(--color-coral, #D85C4A);
          }
          
          .collection-category-nav__item.is-active .collection-category-nav__count {
            background: rgba(216, 92, 74, 0.15);
            color: var(--color-coral, #D85C4A);
          }
          
          .collection-category-nav__item:focus-visible {
            outline: 3px solid var(--color-teal, #598085);
            outline-offset: 2px;
          }
          
          /* Tablet: auto-fit with minimum size */
          @media (max-width: 1024px) {
            .collection-category-nav__inner {
              grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
              padding-inline: var(--public-gutter-tablet, 32px);
            }
          }
          
          /* Mobile: fixed 3-column grid for max 2 rows */
          @media (max-width: 640px) {
            .collection-category-nav {
              top: var(--public-header-height-mobile, 78px);
            }
            
            .collection-category-nav__inner {
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 2px;
              padding: 4px 6px;
            }
            
            .collection-category-nav__item {
              min-width: 0;
              min-height: 44px;
              padding: 3px 2px;
              gap: 1px;
              flex-direction: column;
            }
            
            .collection-category-nav__icon {
              width: 16px;
              height: 16px;
            }
            
            .collection-category-nav__icon svg {
              width: 13px;
              height: 13px;
            }
            
            .collection-category-nav__label {
              font-size: 8px;
              line-height: 1.1;
              letter-spacing: 0.02em;
              white-space: nowrap;
            }
            
            .collection-category-nav__count {
              display: none;
            }
          }
          
          /* Scroll margin for sections */
          .collection-section {
            scroll-margin-top: calc(var(--public-header-height, 120px) + var(--collection-nav-height, 68px) + 16px);
          }
          
          @media (max-width: 640px) {
            .collection-section {
              scroll-margin-top: calc(var(--public-header-height-mobile, 78px) + 100px + 16px);
            }
          }
        `}</style>
        
        <div className="collection-category-nav__inner">
          {/* All Items button */}
          <button
            onClick={() => setCategory("All")}
            className={`collection-category-nav__item${activeCategory === "All" ? ' is-active' : ''}`}
            aria-current={activeCategory === "All" ? 'page' : undefined}
            data-testid="category-nav-all"
          >
            <span className="collection-category-nav__icon" aria-hidden="true">
              <Grid />
            </span>
            <span className="collection-category-nav__label">All</span>
            <span className="collection-category-nav__count">{items.length}</span>
          </button>
          
          {/* Dynamic categories from API */}
          {categories.map((cat) => {
            const Icon = getCategoryIcon(cat.name);
            const isActive = isCategoryActive(cat.name);
            const compactLabel = getCompactLabel(cat.name);
            
            return (
              <button
                key={cat.name}
                onClick={() => setCategory(cat.name)}
                className={`collection-category-nav__item${isActive ? ' is-active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`category-nav-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                title={cat.name}
              >
                <span className="collection-category-nav__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="collection-category-nav__label">{compactLabel}</span>
                {cat.count > 0 && (
                  <span className="collection-category-nav__count">{cat.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
      
      {/* ========== COMPACT NAVY MASTHEAD ========== */}
      <section 
        className="collection-hero relative overflow-hidden collection-section"
        style={{ backgroundColor: '#0D273C' }}
        data-testid="collection-masthead"
      >
        <style>{`
          /* BB-067: Compact Collection Masthead */
          .collection-hero {
            min-height: 0;
            padding-block: clamp(16px, 2vw, 32px);
          }
          
          .collection-hero__inner {
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          
          .collection-hero__eyebrow {
            margin-bottom: 4px;
          }
          
          .collection-hero__title {
            margin: 0;
            font-size: clamp(1.5rem, 2.5vw, 2rem);
          }
          
          .collection-hero__decoration {
            margin-block: 6px;
          }
          
          .collection-hero__intro {
            margin: 0;
            max-width: 560px;
            font-size: clamp(0.8125rem, 0.9vw, 0.875rem);
            line-height: 1.45;
          }
          
          .collection-hero__stamp {
            width: 78px;
            height: 78px;
            opacity: 0.28;
          }
          
          .collection-hero__corner {
            width: 80px;
            height: 64px;
            opacity: 0.06;
          }
          
          /* Tablet: 768-1024px */
          @media (max-width: 1024px) {
            .collection-hero {
              padding-block: clamp(20px, 3vw, 36px);
            }
            .collection-hero__stamp {
              width: 68px;
              height: 68px;
            }
            .collection-hero__title {
              font-size: clamp(1.5rem, 3vw, 2rem);
            }
          }
          
          /* Mobile: ≤640px - deterministic hiding */
          @media (max-width: 640px) {
            .collection-hero {
              padding-block: 16px 20px;
            }
            .collection-hero__stamp,
            .collection-hero__corner {
              display: none !important;
            }
            .collection-hero__eyebrow {
              margin-bottom: 4px;
            }
            .collection-hero__title {
              font-size: 1.375rem;
            }
            .collection-hero__decoration {
              margin-block: 6px;
            }
            .collection-hero__intro {
              font-size: 0.8125rem;
              line-height: 1.4;
            }
          }
        `}</style>
        
        {/* Compact date stamp - hidden on mobile via CSS */}
        <div 
          className="collection-hero__stamp absolute top-3 right-3 md:right-6"
          aria-hidden="true"
        >
          <svg width="78" height="78" viewBox="0 0 78 78" fill="none">
            <circle cx="39" cy="39" r="35" stroke="#F5F0E8" strokeWidth="1.25" strokeDasharray="3 3"/>
            <circle cx="39" cy="39" r="28" stroke="#F5F0E8" strokeWidth="0.75"/>
            <text x="39" y="22" textAnchor="middle" fill="#F5F0E8" fontSize="6" fontFamily="serif" letterSpacing="0.08em">HONOLULU</text>
            <text x="39" y="43" textAnchor="middle" fill="#F5F0E8" fontSize="13" fontWeight="500" fontFamily="serif">JAN 14</text>
            <text x="39" y="56" textAnchor="middle" fill="#F5F0E8" fontSize="9" fontFamily="serif">1973</text>
          </svg>
        </div>
        
        {/* Reduced corner decoration - hidden on mobile via CSS */}
        <div 
          className="collection-hero__corner absolute top-0 left-0"
          aria-hidden="true"
        >
          <svg viewBox="0 0 80 64" fill="none" width="80" height="64">
            <path d="M0,32 Q16,16 32,0 L0,0 Z" fill="#F5F0E8"/>
          </svg>
        </div>
        
        <div className="collection-hero__inner relative max-w-6xl mx-auto px-6 sm:px-8">
          <p className="collection-hero__eyebrow overline text-[#8A6928]">The Archive Collection</p>
          <h1 className="collection-hero__title font-display text-[#F5F0E8] font-light tracking-tight">
            Collection
          </h1>
          
          {/* Compact decorative divider */}
          <div className="collection-hero__decoration flex items-center gap-2">
            <div className="h-px w-8 bg-[#D2C19F]" />
            <svg width="10" height="10" viewBox="0 0 10 10" fill="#D2C19F" aria-hidden="true">
              <path d="M5 0.5L6.5 3.5L9.5 4.5L6.5 5.5L5 8.5L3.5 5.5L0.5 4.5L3.5 3.5L5 0.5Z"/>
            </svg>
            <div className="h-px w-8 bg-[#D2C19F]" />
          </div>
          
          <p className="collection-hero__intro font-body text-sm sm:text-base text-[#F5F0E8]/80">
            Explore authentic artifacts and memorabilia from Elvis Presley's Aloha from Hawaii event.
          </p>
        </div>
      </section>

      {/* ========== COMPACT SEARCH & FILTERS ========== */}
      <section className="collection-toolbar bg-[#F5F0E8] border-b border-[#D8CDBD]" style={{ padding: '12px 0' }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          
          {/* Search, Sort, Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A38B7C]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the collection..."
                className="archive-input w-full pl-9 pr-3 py-2 text-sm"
                data-testid="gallery-search"
              />
            </div>
            
            {/* Quick Filters */}
            <div className="hidden lg:flex items-center gap-1.5">
              <button
                onClick={() => toggle("featured")}
                className={`flex items-center gap-1 px-2.5 py-1.5 font-body text-xs uppercase tracking-wide transition-colors ${
                  flags.featured 
                    ? "bg-[#D85C4A] text-white" 
                    : "border border-[#D8CDBD] text-[#7D6352] hover:border-[#D85C4A] hover:text-[#D85C4A]"
                }`}
                data-testid="filter-featured"
              >
                <Star className="w-3 h-3" />
                Featured
              </button>
              <button
                onClick={() => toggle("graceland")}
                className={`flex items-center gap-1 px-2.5 py-1.5 font-body text-xs uppercase tracking-wide transition-colors ${
                  flags.graceland 
                    ? "bg-[#D85C4A] text-white" 
                    : "border border-[#D8CDBD] text-[#7D6352] hover:border-[#D85C4A] hover:text-[#D85C4A]"
                }`}
                data-testid="filter-graceland"
              >
                <Award className="w-3 h-3" />
                Graceland
              </button>
              <button
                onClick={() => toggle("psa")}
                className={`flex items-center gap-1 px-2.5 py-1.5 font-body text-xs uppercase tracking-wide transition-colors ${
                  flags.psa 
                    ? "bg-[#D85C4A] text-white" 
                    : "border border-[#D8CDBD] text-[#7D6352] hover:border-[#D85C4A] hover:text-[#D85C4A]"
                }`}
                data-testid="filter-psa"
              >
                <FileCheck className="w-3 h-3" />
                PSA
              </button>
            </div>
            
            <div className="flex items-center gap-2 ml-auto">
              <span className="font-body text-xs text-[#7D6352] hidden sm:inline">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="archive-input text-xs py-1.5 pr-6"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="title">Title A-Z</option>
              </select>
              
              {/* View Mode */}
              <div className="flex border border-[#D8CDBD]">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-1.5 transition-colors ${viewMode === "grid" ? "bg-[#598085] text-white" : "bg-[#F5F0E8] text-[#7D6352] hover:bg-[#EAE3D5]"}`}
                  aria-label="Grid view"
                >
                  <Grid className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-1.5 transition-colors ${viewMode === "list" ? "bg-[#598085] text-white" : "bg-[#F5F0E8] text-[#7D6352] hover:bg-[#EAE3D5]"}`}
                  aria-label="List view"
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              </div>
              
              <span className="font-body text-xs text-[#7D6352]" data-testid="gallery-count">
                {filtered.length} item{filtered.length !== 1 && "s"}
              </span>
            </div>
          </div>
          
          {/* Clear Filters - more compact */}
          {hasActiveFilters && (
            <div className="mt-2 pt-2 border-t border-[#D8CDBD]">
              <button
                onClick={clearFilters}
                className="font-body text-xs text-[#D85C4A] hover:text-[#5A4133] transition-colors"
                data-testid="clear-filters"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ========== COLLECTION GRID ========== */}
      <section className="collection-results collection-section" style={{ paddingTop: 'clamp(20px, 4vw, 36px)', paddingBottom: 'clamp(32px, 6vw, 48px)' }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D8CDBD] border-t-[#598085]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 text-[#D8CDBD] mx-auto mb-3" />
              <p className="font-body text-sm text-[#7D6352] mb-3">No artifacts match your current filters.</p>
              <button onClick={clearFilters} className="font-body text-xs text-[#D85C4A] hover:underline">
                Clear all filters
              </button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 stagger-reveal">
              {filtered.map((item) => (
                <ArtifactCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((item) => (
                <ArtifactListItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Hibiscus Divider */}
      <div className="flex items-center justify-center py-8">
        <svg width="80" height="24" viewBox="0 0 80 24" fill="none">
          <line x1="0" y1="12" x2="25" y2="12" stroke="#D2C19F" strokeWidth="1" />
          <path d="M40 4c-2 0-4 1.5-5 3.5-1-2-3-3.5-5-3.5-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5c2 0 4-1.5 5-3.5 1 2 3 3.5 5 3.5 2.5 0 4.5-2 4.5-4.5S42.5 4 40 4z" fill="#E77143"/>
          <line x1="55" y1="12" x2="80" y2="12" stroke="#D2C19F" strokeWidth="1" />
        </svg>
      </div>
    </div>
  );
};

/* ============================================================================
   Artifact Card Component — Per mockup design
   ============================================================================ */

const ArtifactCard = ({ item }) => {
  const hasImage = item.images?.[0]?.url;
  
  return (
    <Link
      to={`/collection/item/${item.slug}`}
      className="artifact-card group animate-fadeIn"
      data-testid={`artifact-card-${item.slug}`}
    >
      {/* Status Badge */}
      {item.graceland_displayed && (
        <span className="status-badge">Graceland</span>
      )}
      {item.featured && !item.graceland_displayed && (
        <span className="status-badge" style={{ borderColor: '#3A7A8E', color: '#3A7A8E' }}>Featured</span>
      )}
      
      {/* Image */}
      <div className="aspect-[4/5] mb-4 overflow-hidden bg-[#EAE3D5] border border-[#D8CDBD]">
        {hasImage ? (
          <img
            src={item.images[0].url}
            alt={item.title}
            className="w-full h-full object-cover archival-image-hover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Ticket className="w-12 h-12 text-[#D8CDBD]" />
          </div>
        )}
      </div>
      
      {/* Content */}
      <h4 className="font-display text-lg text-[#5A4133] group-hover:text-[#3A7A8E] transition-colors leading-snug mb-1">
        {item.title}
      </h4>
      {item.short_description ? (
        <p className="font-body text-xs text-[#A38B7C] mb-3">
          {item.short_description.slice(0, 60)}
        </p>
      ) : null}
      <p className="font-body text-xs text-[#7D6352] mb-3">
        {item.date_label || "Date not established"}
      </p>
      
      {/* Verification Status */}
      {item.verification_status && (
        <span className={`inline-block ${
          item.verification_status === 'documented' 
            ? 'status-documented' 
            : item.verification_status === 'pending'
            ? 'status-pending'
            : 'status-qualified'
        }`}>
          {item.verification_status === 'documented' ? 'Documented' : 
           item.verification_status === 'pending' ? 'Verification Pending' : 'Qualified'}
        </span>
      )}
    </Link>
  );
};

/* ============================================================================
   Artifact List Item — Horizontal layout
   ============================================================================ */

const ArtifactListItem = ({ item }) => {
  const hasImage = item.images?.[0]?.url;
  
  return (
    <Link
      to={`/collection/item/${item.slug}`}
      className="group flex gap-6 p-4 bg-[#F5F0E8] border border-[#D8CDBD] hover:border-[#3A7A8E] transition-colors"
      data-testid={`artifact-list-${item.slug}`}
    >
      {/* Thumbnail */}
      <div className="w-24 h-24 shrink-0 overflow-hidden bg-[#EAE3D5] border border-[#D8CDBD]">
        {hasImage ? (
          <img src={item.images[0].url} alt={item.title} className="w-full h-full object-cover archival-image" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Ticket className="w-8 h-8 text-[#D8CDBD]" />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-body text-xs uppercase tracking-wide text-[#3A7A8E] mb-1">{item.category}</p>
            <h4 className="font-display text-lg text-[#5A4133] group-hover:text-[#3A7A8E] transition-colors">
              {item.title}
            </h4>
          </div>
          {item.graceland_displayed && (
            <span className="bg-[#D2C19F] text-white px-2 py-1 font-body text-[10px] uppercase tracking-wide">
              Graceland
            </span>
          )}
        </div>
        
        {item.short_description && (
          <p className="font-body text-sm text-[#7D6352] mt-2 line-clamp-2">{item.short_description}</p>
        )}
        
        <div className="flex items-center gap-4 mt-3">
          <span className="font-body text-xs text-[#A38B7C]">{item.date_label || "Date not established"}</span>
          {item.verification_status && (
            <span className={`${
              item.verification_status === 'documented' ? 'status-documented' : 
              item.verification_status === 'pending' ? 'status-pending' : 'status-qualified'
            }`}>
              {item.verification_status === 'documented' ? 'Documented' : 
               item.verification_status === 'pending' ? 'Pending' : 'Qualified'}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default CollectionGalleryPage;
